import { type FC, useState, useEffect, useRef, useContext, memo } from 'react';
import { utils } from 'ethers';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle';
import { Link } from 'dibs-elements/exports/Link';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { usePriceLabel, useTooltip } from '../../helpers/priceHelpers';
import { NftPdpBidModalContext } from '../NftPdpBidModal/NftPdpBidModalContext';
import { type NftPdpPriceDisplay_nftItem$key } from './__generated__/NftPdpPriceDisplay_nftItem.graphql';
import styles from './NftPdpPriceDisplay.scss';

type Props = {
    nftItem: NftPdpPriceDisplay_nftItem$key;
};

export const NftPdpPriceDisplay: FC<Props> = memo(({ nftItem: nftItemRef }) => {
    const {
        getAmountStrings,
        pendingCurrentBid,
        currentBidWei,
        auctionStatus,
        isAwaitingSettlement,
        isHighestBidder,
        isOwner: isOwnerProp,
    } = useContext(NftPdpBidModalContext);
    const [showTooltip, setShowTooltip] = useState(false);
    const closeTooltipRef = useRef((e: KeyboardEvent) => {
        if (e.key === 'Escape') setShowTooltip(false);
    });
    useEffect(() => {
        if (showTooltip) {
            window.addEventListener('keydown', closeTooltipRef.current);
        } else {
            window.removeEventListener('keydown', closeTooltipRef.current);
        }
    }, [showTooltip]);
    const nftItem = useFragment(
        graphql`
            fragment NftPdpPriceDisplay_nftItem on NftItem {
                mostRecentAuction {
                    reservePrice {
                        ether
                    }
                }
            }
        `,
        nftItemRef
    );
    const { mostRecentAuction } = nftItem || {};

    const { reservePrice } = mostRecentAuction || {};

    const hasCurrentBid = !!currentBidWei;
    const hasReservePrice = !!reservePrice;
    const hasPendingBid = !!pendingCurrentBid;
    const isOwner = isOwnerProp || (isHighestBidder && isAwaitingSettlement);
    const isEnded = auctionStatus === 'ENDED' || isAwaitingSettlement;
    const isCancelled = auctionStatus === 'CANCELED';
    const priceLabel = usePriceLabel({
        isEnded,
        hasCurrentBid,
        hasPendingBid,
        isOwner,
    });
    const tooltip = useTooltip({ isEnded, hasCurrentBid, hasReservePrice, isOwner });
    const etherAmount =
        (pendingCurrentBid && utils.formatEther(pendingCurrentBid)) ||
        (currentBidWei && utils.formatEther(currentBidWei)) ||
        reservePrice?.ether;
    const { etherWithConversion } =
        !isCancelled && etherAmount
            ? getAmountStrings(`${etherAmount}`)
            : {
                  etherWithConversion: (
                      <FormattedMessage id="nft.pdp.priceDisplay.notSet" defaultMessage="Not set" />
                  ),
              };
    return (
        <>
            <div className={styles.priceLabelWrapper}>
                <span className={styles.priceLabel}>{priceLabel}</span>
                <div
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    className={styles.tooltipWrapper}
                >
                    <Link
                        className={styles.tooltipTrigger}
                        onFocus={() => setShowTooltip(true)}
                        onBlur={() => setShowTooltip(false)}
                        onClick={() => {}}
                        ariaLabel={`${priceLabel} Info`}
                        ariaDescribedBy="nftPriceDisplayTooltipContent"
                    >
                        <InfoCircle className={styles.infoIcon} />
                    </Link>
                    <Tooltip isVisible={showTooltip} direction="right">
                        <div className={styles.tooltipContent} id="nftPriceDisplayTooltipContent">
                            {tooltip}
                        </div>
                    </Tooltip>
                </div>
            </div>
            <div className={styles.priceAmount}>
                {etherWithConversion}
                {hasPendingBid && (
                    <Spinner size="tiny" black containerClass={styles.pendingSpinner} />
                )}
            </div>
        </>
    );
});
